// src/hooks/useSelfHealingPreview.ts
// Self-healing system: detects runtime/build errors and auto-fixes them
// without user input. Max 2 AI attempts, then surfaces to user.

import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { SandboxConsoleLog } from "./useSandboxPreview";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type SelfHealErrorType =
  | "build_esbuild"
  | "runtime_js"
  | "missing_module"
  | "missing_export"
  | "duplicate_identifier"
  | "not_a_function"
  | "unknown";

export interface DetectedError {
  message: string;
  type: SelfHealErrorType;
  file?: string;
  timestamp: number;
}

export interface SelfHealState {
  /** Current status of the self-healing system */
  status: "idle" | "detecting" | "fixing" | "fixed" | "failed";
  /** Number of auto-fix attempts made for the current error */
  attempts: number;
  /** The error being addressed */
  currentError: DetectedError | null;
  /** Whether the user can manually trigger a fix */
  canManualFix: boolean;
}

interface ProjectFile {
  name: string;
  content: string;
}

interface HardcodedFix {
  file: string;
  content: string;
}

// ---------------------------------------------------------------------------
// Hardcoded fix patterns (no AI needed)
// ---------------------------------------------------------------------------

const SHADCN_COMPONENTS = [
  "button", "card", "input", "textarea", "badge", "table",
  "select", "dialog", "tabs", "alert", "avatar", "separator",
];

/**
 * Try to fix the error with a hardcoded pattern.
 * Returns the fixed files if successful, null if the error is not a known pattern.
 */
function tryHardcodedFix(
  error: DetectedError,
  files: ProjectFile[]
): { fixes: HardcodedFix[]; description: string } | null {
  const msg = error.message;

  // 1. Missing shadcn component import: "Cannot find module './components/ui/X'"
  const missingModuleMatch = msg.match(
    /(?:Cannot find module|Could not resolve)[:\s]*["']?(?:@\/|\.\/|src\/)?components\/ui\/(\w+)["']?/i
  );
  if (missingModuleMatch) {
    const componentName = missingModuleMatch[1].toLowerCase();
    if (SHADCN_COMPONENTS.includes(componentName)) {
      // The component should already be in the scaffold — this means the file
      // exists but the import path is wrong. No file content fix needed; the
      // esbuild plugin resolves @/ aliases. This is likely a casing issue.
      // Return null to let the AI handle it.
      return null;
    }
  }

  // 2. "X is not a function" on a useState setter — e.g., "setCount is not a function"
  const notAFuncMatch = msg.match(/(\w+)\s+is\s+not\s+a\s+function/i);
  if (notAFuncMatch) {
    const funcName = notAFuncMatch[1];
    // Check if it looks like a useState setter (starts with "set" + capital letter)
    if (/^set[A-Z]/.test(funcName)) {
      // Find the file that uses this setter
      for (const file of files) {
        if (!file.name.endsWith(".tsx") && !file.name.endsWith(".ts")) continue;
        if (!file.content.includes(funcName)) continue;

        // Check for destructuring error: const [value, setValue] = useState(...)
        // Common mistake: const [setValue, value] = useState(...) — swapped order
        const stateVarName = funcName.replace(/^set/, "");
        const lowerFirst = stateVarName.charAt(0).toLowerCase() + stateVarName.slice(1);
        const swappedPattern = new RegExp(
          `const\\s*\\[\\s*${funcName}\\s*,\\s*${lowerFirst}\\s*\\]\\s*=\\s*useState`,
        );
        if (swappedPattern.test(file.content)) {
          const fixed = file.content.replace(
            swappedPattern,
            `const [${lowerFirst}, ${funcName}] = useState`,
          );
          return {
            fixes: [{ file: file.name, content: fixed }],
            description: `Swapped useState destructuring order for ${funcName}`,
          };
        }
      }
    }
  }

  // 3. Missing default export: "does not provide an export named 'default'"
  const missingDefaultMatch = msg.match(
    /(?:does not provide|does not have).*export.*(?:named\s+)?['"]?default['"]?/i
  );
  if (missingDefaultMatch && error.file) {
    const file = files.find(
      (f) => f.name === error.file || f.name === `src/${error.file}`,
    );
    if (file) {
      // Find the component name (first function/const that returns JSX)
      const componentMatch = file.content.match(
        /(?:export\s+)?(?:function|const)\s+([A-Z]\w+)/,
      );
      if (componentMatch) {
        const name = componentMatch[1];
        if (!file.content.includes("export default")) {
          const fixed = file.content + `\n\nexport default ${name};\n`;
          return {
            fixes: [{ file: file.name, content: fixed }],
            description: `Added missing default export for ${name}`,
          };
        }
      }
    }
  }

  // 4. Duplicate identifier: "Identifier 'X' has already been declared"
  const dupMatch = msg.match(
    /(?:Identifier|Duplicate declaration)\s+['"]?(\w+)['"]?\s+has already been declared/i,
  );
  if (dupMatch && error.file) {
    const identName = dupMatch[1];
    const file = files.find(
      (f) => f.name === error.file || f.name === `src/${error.file}`,
    );
    if (file) {
      // Find duplicate declarations — keep the first, rename the second
      const lines = file.content.split("\n");
      let firstFound = false;
      const fixedLines = lines.map((line) => {
        const declPattern = new RegExp(
          `(const|let|var|function)\\s+${identName}\\b`,
        );
        if (declPattern.test(line)) {
          if (!firstFound) {
            firstFound = true;
            return line;
          }
          // Rename the duplicate
          return line.replace(
            new RegExp(`\\b${identName}\\b`),
            `${identName}_2`,
          );
        }
        return line;
      });
      const fixed = fixedLines.join("\n");
      if (fixed !== file.content) {
        return {
          fixes: [{ file: file.name, content: fixed }],
          description: `Renamed duplicate identifier '${identName}'`,
        };
      }
    }
  }

  return null;
}

// ---------------------------------------------------------------------------
// Classify error type
// ---------------------------------------------------------------------------

function classifyError(message: string): SelfHealErrorType {
  const m = message.toLowerCase();
  if (m.includes("could not resolve") || m.includes("cannot find module"))
    return "missing_module";
  if (m.includes("does not provide an export") || m.includes("does not have a default export"))
    return "missing_export";
  if (m.includes("is not a function")) return "not_a_function";
  if (m.includes("has already been declared") || m.includes("duplicate declaration"))
    return "duplicate_identifier";
  if (m.includes("[esbuild]") || m.includes("build failed") || m.includes("parse error"))
    return "build_esbuild";
  return "runtime_js";
}

/**
 * Extract a likely file path from an error message.
 */
function extractFilePath(message: string): string | undefined {
  // Match patterns like "in src/App.tsx", "at src/pages/Home.tsx:12:5", "./components/Shell"
  const patterns = [
    /(?:in|at|from)\s+(src\/[\w/.-]+\.\w+)/i,
    /["'](src\/[\w/.-]+\.\w+)["']/,
    /["'](\.\/[\w/.-]+\.\w+)["']/,
    /([\w/.-]+\.tsx?):\d+:\d+/,
  ];
  for (const re of patterns) {
    const m = message.match(re);
    if (m) return m[1].replace(/^\.\//, "");
  }
  return undefined;
}

/**
 * Search project files for patterns that match the error.
 * Used when error message contains minified variable names (e.g., "Ut.pages.length").
 */
function findFileByErrorPattern(
  message: string,
  files: ProjectFile[],
): string | undefined {
  // Extract property access patterns from error (e.g., ".pages.length", ".items.map")
  const propMatch = message.match(/\.(\w+)\.(\w+)/);
  if (!propMatch) return undefined;

  const prop1 = propMatch[1]; // e.g., "pages"
  const prop2 = propMatch[2]; // e.g., "length"

  // Search files for unguarded property access patterns
  const dangerousPatterns = [
    new RegExp(`\\.${prop1}\\.${prop2}`, "g"),           // .pages.length
    new RegExp(`\\.${prop1}\\s*\\.\\s*${prop2}`, "g"),   // .pages . length
    new RegExp(`\\b${prop1}\\s*\\.\\s*${prop2}`, "g"),   // pages.length (bare)
  ];

  for (const file of files) {
    if (!file.name.endsWith(".tsx") && !file.name.endsWith(".ts")) continue;
    if (file.name.includes("/ui/")) continue; // Skip UI library files

    for (const pattern of dangerousPatterns) {
      if (pattern.test(file.content)) {
        // Verify it's not safely guarded (e.g., "pages && pages.length")
        const safePattern = new RegExp(`${prop1}\\s*&&\\s*${prop1}\\.${prop2}`, "g");
        const optionalPattern = new RegExp(`${prop1}\\?\\.${prop2}`, "g");
        if (!safePattern.test(file.content) && !optionalPattern.test(file.content)) {
          console.log(`[selfHeal] Found unguarded .${prop1}.${prop2} in ${file.name}`);
          return file.name;
        }
      }
    }
  }

  return undefined;
}

// ---------------------------------------------------------------------------
// Error deduplication
// ---------------------------------------------------------------------------

/** Normalize an error message for dedup (strip line numbers, whitespace). */
function normalizeForDedup(msg: string): string {
  return msg
    .replace(/:\d+:\d+/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

// ---------------------------------------------------------------------------
// Log error to Supabase
// ---------------------------------------------------------------------------

async function logBuildError(
  projectId: string,
  error: DetectedError,
  autoFixed: boolean,
): Promise<void> {
  try {
    await supabase.from("build_errors").insert({
      project_id: projectId,
      error_message: error.message.slice(0, 2000),
      error_type: error.type,
      auto_fixed: autoFixed,
    });
  } catch (e) {
    console.warn("[selfHeal] Failed to log build error:", e);
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

const MAX_AUTO_FIX_ATTEMPTS = 2;
const ERROR_DETECTION_DELAY_MS = 1000; // Wait 1s after error before attempting fix (reduced from 3s for faster feedback)
const IMMEDIATE_CHECK_DELAY_MS = 800; // Wait 800ms after immediate check request
const COOLDOWN_MS = 10000; // Min gap between auto-fix cycles

export interface UseSelfHealingPreviewOptions {
  projectId: string | null;
  /** Current project files */
  getProjectFiles: () => ProjectFile[];
  /** Sandbox console logs from useSandboxPreview */
  consoleLogs: SandboxConsoleLog[];
  /** Sandbox error from useSandboxPreview */
  sandboxError: string | null;
  /** Current builder status — don't self-heal while AI is generating */
  builderStatus: string;
  /** Callback to apply file edits and rebuild preview */
  onApplyFixes: (
    fixes: Array<{ filePath: string; action: "modify"; content: string; explanation: string }>,
  ) => Promise<{ buildSuccess: boolean }>;
  /** Callback to send an AI fix instruction */
  onSendFixInstruction: (
    errorMessage: string,
    errorFile: string | undefined,
    files: ProjectFile[],
  ) => Promise<{ edits: Array<{ filePath: string; content: string }> } | null>;
  /** Whether self-healing is enabled (disable during manual user edits) */
  enabled?: boolean;
}

export function useSelfHealingPreview(options: UseSelfHealingPreviewOptions) {
  const {
    projectId,
    getProjectFiles,
    consoleLogs,
    sandboxError,
    builderStatus,
    onApplyFixes,
    onSendFixInstruction,
    enabled = true,
  } = options;

  const [state, setState] = useState<SelfHealState>({
    status: "idle",
    attempts: 0,
    currentError: null,
    canManualFix: false,
  });

  const lastFixedErrorRef = useRef<string | null>(null);
  const cooldownUntilRef = useRef<number>(0);
  const fixInProgressRef = useRef(false);
  const detectionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Track which errors we've already processed (dedup)
  const processedErrorsRef = useRef<Set<string>>(new Set());

  // Reset when project changes
  useEffect(() => {
    processedErrorsRef.current.clear();
    lastFixedErrorRef.current = null;
    cooldownUntilRef.current = 0;
    setState({
      status: "idle",
      attempts: 0,
      currentError: null,
      canManualFix: false,
    });
  }, [projectId]);

  // ---------------------------------------------------------------------------
  // Error detection: watch consoleLogs for runtime errors
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!enabled || !projectId) return;
    // Don't detect while AI is actively generating or fixing
    if (["generating", "applying", "building", "fixing"].includes(builderStatus)) return;
    if (fixInProgressRef.current) return;

    // Look for new error-level console logs
    const errorLogs = consoleLogs.filter((l) => l.type === "error");
    if (errorLogs.length === 0 && !sandboxError) return;

    // Use the most recent error
    const latestError = sandboxError || errorLogs[errorLogs.length - 1]?.message;
    if (!latestError) return;

    // Skip React double-mount warnings
    if (
      latestError.includes("ReactDOMClient.createRoot") &&
      latestError.includes("already been passed to createRoot")
    ) return;

    // Skip errors we already processed
    const dedupKey = normalizeForDedup(latestError);
    if (processedErrorsRef.current.has(dedupKey)) return;
    if (lastFixedErrorRef.current === dedupKey) return;

    // Cooldown check
    if (Date.now() < cooldownUntilRef.current) return;

    // Clear any existing detection timer
    if (detectionTimerRef.current) {
      clearTimeout(detectionTimerRef.current);
    }

    // Wait before attempting fix (give time for multiple errors to settle)
    setState((prev) => ({ ...prev, status: "detecting" }));
    detectionTimerRef.current = setTimeout(() => {
      processError(latestError, dedupKey);
    }, ERROR_DETECTION_DELAY_MS);

    return () => {
      if (detectionTimerRef.current) {
        clearTimeout(detectionTimerRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consoleLogs.length, sandboxError, enabled, projectId, builderStatus]);

  // ---------------------------------------------------------------------------
  // Process a detected error
  // ---------------------------------------------------------------------------
  const processError = useCallback(
    async (errorMessage: string, dedupKey: string) => {
      if (fixInProgressRef.current) return;
      if (!projectId) return;

      fixInProgressRef.current = true;
      processedErrorsRef.current.add(dedupKey);

      const errorType = classifyError(errorMessage);
      const files = getProjectFiles();

      // Try to extract file path from error message
      let errorFile = extractFilePath(errorMessage);

      // If no file path found (minified error like "Ut.pages.length"),
      // search project files for the problematic pattern
      if (!errorFile) {
        errorFile = findFileByErrorPattern(errorMessage, files);
        if (errorFile) {
          console.log(`[selfHeal] Found culprit file via pattern search: ${errorFile}`);
        }
      }

      const detectedError: DetectedError = {
        message: errorMessage,
        type: errorType,
        file: errorFile,
        timestamp: Date.now(),
      };

      setState({
        status: "fixing",
        attempts: 0,
        currentError: detectedError,
        canManualFix: false,
      });

      // Step 1: Try hardcoded fix
      const hardcoded = tryHardcodedFix(detectedError, files);
      if (hardcoded) {
        console.log(`[selfHeal] Applying hardcoded fix: ${hardcoded.description}`);
        const fixEdits = hardcoded.fixes.map((f) => ({
          filePath: f.file,
          action: "modify" as const,
          content: f.content,
          explanation: hardcoded.description,
        }));
        const result = await onApplyFixes(fixEdits);
        await logBuildError(projectId, detectedError, result.buildSuccess);

        if (result.buildSuccess) {
          lastFixedErrorRef.current = dedupKey;
          cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
          setState({
            status: "fixed",
            attempts: 1,
            currentError: detectedError,
            canManualFix: false,
          });
          fixInProgressRef.current = false;
          return;
        }
        // Hardcoded fix didn't work — fall through to AI fix
      }

      // Step 2: AI fix (up to MAX_AUTO_FIX_ATTEMPTS)
      // Defensive: detect useless error messages that would produce empty prompts
      const isUselessError = !errorMessage ||
        errorMessage.trim() === "{}" ||
        errorMessage.trim() === "[object Object]" ||
        errorMessage.trim().length < 5;

      if (isUselessError) {
        console.error("[selfHeal] Received useless error message, aborting AI fix:", JSON.stringify(errorMessage));
        cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
        setState({
          status: "failed",
          attempts: 0,
          currentError: detectedError,
          canManualFix: false,
        });
        fixInProgressRef.current = false;
        return;
      }

      for (let attempt = 1; attempt <= MAX_AUTO_FIX_ATTEMPTS; attempt++) {
        console.log(`[selfHeal] AI fix attempt ${attempt}/${MAX_AUTO_FIX_ATTEMPTS}`);
        setState((prev) => ({
          ...prev,
          status: "fixing",
          attempts: attempt,
        }));

        const currentFiles = getProjectFiles();
        const fixResult = await onSendFixInstruction(
          errorMessage,
          errorFile,
          currentFiles,
        );

        if (!fixResult?.edits?.length) {
          console.log("[selfHeal] AI returned no edits");
          continue;
        }

        const fixEdits = fixResult.edits.map((e) => ({
          filePath: e.filePath,
          action: "modify" as const,
          content: e.content,
          explanation: `Auto-fix attempt ${attempt}: ${errorMessage.slice(0, 100)}`,
        }));

        const applyResult = await onApplyFixes(fixEdits);
        if (applyResult.buildSuccess) {
          lastFixedErrorRef.current = dedupKey;
          cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
          await logBuildError(projectId, detectedError, true);
          setState({
            status: "fixed",
            attempts: attempt,
            currentError: detectedError,
            canManualFix: false,
          });
          fixInProgressRef.current = false;
          return;
        }
      }

      // All attempts failed — surface to user
      await logBuildError(projectId, detectedError, false);
      cooldownUntilRef.current = Date.now() + COOLDOWN_MS;
      setState({
        status: "failed",
        attempts: MAX_AUTO_FIX_ATTEMPTS,
        currentError: detectedError,
        canManualFix: true,
      });
      fixInProgressRef.current = false;
    },
    [projectId, getProjectFiles, onApplyFixes, onSendFixInstruction],
  );

  // ---------------------------------------------------------------------------
  // Manual fix (user clicks "Fix errors" after auto-fix failed)
  // ---------------------------------------------------------------------------
  const triggerManualFix = useCallback(async () => {
    if (!state.currentError || !projectId) return;
    fixInProgressRef.current = true;
    setState((prev) => ({ ...prev, status: "fixing", canManualFix: false }));

    const files = getProjectFiles();

    // Defensive: ensure error message is never "{}" or empty (Error serialization bug)
    const rawMessage = state.currentError.message;
    const sanitizedMessage = (!rawMessage || rawMessage === "{}" || rawMessage === "[object Object]")
      ? "Unknown runtime error — no details captured"
      : rawMessage;

    const fixResult = await onSendFixInstruction(
      sanitizedMessage,
      state.currentError.file,
      files,
    );

    if (fixResult?.edits?.length) {
      const fixEdits = fixResult.edits.map((e) => ({
        filePath: e.filePath,
        action: "modify" as const,
        content: e.content,
        explanation: `Manual fix: ${state.currentError!.message.slice(0, 100)}`,
      }));
      const result = await onApplyFixes(fixEdits);
      const autoFixed = result.buildSuccess;
      await logBuildError(projectId, state.currentError, autoFixed);
      setState((prev) => ({
        ...prev,
        status: autoFixed ? "fixed" : "failed",
        canManualFix: !autoFixed,
      }));
    } else {
      setState((prev) => ({ ...prev, status: "failed", canManualFix: false }));
    }
    fixInProgressRef.current = false;
  }, [state.currentError, projectId, getProjectFiles, onSendFixInstruction, onApplyFixes]);

  // ---------------------------------------------------------------------------
  // Reset (user clicks "Start fresh")
  // ---------------------------------------------------------------------------
  const resetSelfHeal = useCallback(() => {
    if (detectionTimerRef.current) clearTimeout(detectionTimerRef.current);
    processedErrorsRef.current.clear();
    lastFixedErrorRef.current = null;
    fixInProgressRef.current = false;
    setState({
      status: "idle",
      attempts: 0,
      currentError: null,
      canManualFix: false,
    });
  }, []);

  // ---------------------------------------------------------------------------
  // Immediate check (called after edits applied — faster than normal detection)
  // ---------------------------------------------------------------------------
  const checkForErrorsNow = useCallback(() => {
    if (!enabled || !projectId) return;
    if (fixInProgressRef.current) return;

    // Clear processed errors to allow re-detection of same error after new edit
    processedErrorsRef.current.clear();

    // Short delay to let errors propagate from preview
    if (detectionTimerRef.current) clearTimeout(detectionTimerRef.current);
    detectionTimerRef.current = setTimeout(() => {
      const errorLogs = consoleLogs.filter((l) => l.type === "error");
      const latestError = sandboxError || errorLogs[errorLogs.length - 1]?.message;
      if (!latestError) return;

      // Skip React double-mount warnings
      if (
        latestError.includes("ReactDOMClient.createRoot") &&
        latestError.includes("already been passed to createRoot")
      ) return;

      const dedupKey = normalizeForDedup(latestError);
      if (lastFixedErrorRef.current === dedupKey) return;

      console.log("[selfHeal] Immediate check found error:", latestError.slice(0, 100));
      processError(latestError, dedupKey);
    }, IMMEDIATE_CHECK_DELAY_MS);
  }, [enabled, projectId, consoleLogs, sandboxError, processError]);

  return {
    selfHealState: state,
    triggerManualFix,
    resetSelfHeal,
    checkForErrorsNow,
  };
}
